import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, Typography, Grid, Card, CardContent, Divider, 
  LinearProgress, List, ListItem, ListItemText, 
  ListItemIcon, Chip, useTheme, Skeleton, Alert,
  Button, Tabs, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { 
  Storage as StorageIcon, 
  Memory as MemoryIcon, 
  Dns as DnsIcon, 
  Warning as WarningIcon, 
  Security as SecurityIcon, 
  AccessTime as AccessTimeIcon,
  InsertChart as ChartIcon,
  Cloud as CloudIcon,
  Lock as LockIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  Notifications as NotificationsIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { 
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, 
  Tooltip as RechartsTooltip, RadialBarChart, RadialBar,
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  CartesianGrid, Legend, ComposedChart
} from 'recharts';
import { UserContext } from '../../../contexts/UserContext';
import { fetchJson } from '../../../utils/fetchJson';

// Custom chart components
const UptimeSparkline = ({ data }) => (
  <ResponsiveContainer width="100%" height={120}>
    <AreaChart data={data} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
      <defs>
        <linearGradient id="uptimeGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8}/>
          <stop offset="95%" stopColor="#4caf50" stopOpacity={0.1}/>
        </linearGradient>
      </defs>
      <Area 
        type="monotone" 
        dataKey="uptime" 
        stroke="#4caf50" 
        fill="url(#uptimeGradient)" 
        strokeWidth={2}
        dot={{ strokeWidth: 2, r: 3, fill: '#fff' }}
        activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
      />
      <XAxis dataKey="day" />
      <YAxis domain={[95, 100]} />
      <RechartsTooltip 
        content={({ payload }) => (
          payload && payload[0] ? (
            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              boxShadow: 3,
              borderRadius: 1,
              padding: 1
            }}>
              <Typography variant="body2">
                <strong>Day {payload[0].payload.day}</strong>: {payload[0].value}%
              </Typography>
            </Card>
          ) : null
        )}
      />
    </AreaChart>
  </ResponsiveContainer>
);

const ResourceRadialGauge = ({ value, name, max = 100, color }) => (
  <Box sx={{ textAlign: 'center' }}>
    <ResponsiveContainer width={120} height={120}>
      <RadialBarChart 
        innerRadius="80%" 
        outerRadius="100%" 
        barSize={12}
        data={[{ name, value, fill: color }]}
        startAngle={180} 
        endAngle={0}
      >
        <RadialBar 
          background 
          dataKey="value" 
          cornerRadius={10}
        />
        <text 
          x="50%" 
          y="50%" 
          textAnchor="middle" 
          dominantBaseline="middle"
          fill={value > 80 ? '#f44336' : '#666'}
          fontWeight={600}
          fontSize={18}
        >
          {value}%
        </text>
      </RadialBarChart>
    </ResponsiveContainer>
    <Typography variant="body2" mt={-1}>{name}</Typography>
  </Box>
);

export default function SystemOverviewPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const user = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedSchool, setSelectedSchool] = useState('all');
  const [globalYears, setGlobalYears] = useState([]);
  const [selectedGlobalYearId, setSelectedGlobalYearId] = useState('');
  const [schools, setSchools] = useState([]);
  const [crossSchoolData, setCrossSchoolData] = useState(null);
  const [systemAlerts, setSystemAlerts] = useState([]);
  const [crossSchoolError, setCrossSchoolError] = useState('');
  const [platformData, setPlatformData] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(false);
  
  const [alerts, setAlerts] = useState([]);

  // Check System Admin permissions and load all real data
  useEffect(() => {
    const init = async () => {
      if (!user) { setLoading(false); return; }
      const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
      const allowed = user.role === 'SystemAdmin' || !perms || perms.includes('*') || perms.includes('system-overview');
      setHasPermission(allowed);
      if (allowed) {
        await Promise.all([loadSchools(), loadPlatformAnalytics(), loadSystemAlerts()]);
      }
      setLoading(false);
    };
    init();
  }, [user]);

  const canFeature = React.useCallback((permKey) => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes(permKey);
  }, [user]);

  const canManageOverview = canFeature('system-overview');

  useEffect(() => {
    if (!hasPermission) return;
    loadCrossSchoolData();
  }, [selectedSchool, selectedGlobalYearId, hasPermission]);

  useEffect(() => {
    if (!hasPermission) return;
    loadGlobalAcademicYears();
  }, [hasPermission]);

  const loadPlatformAnalytics = async () => {
    try {
      const data = await fetchJson('/api/super-admin/platform-analytics');
      setPlatformData(data);
    } catch (error) {
      console.error('Failed to load platform analytics:', error);
    }
  };

  const loadGlobalAcademicYears = async () => {
    try {
      const data = await fetchJson('/api/global-academic-years');
      const list = Array.isArray(data) ? data : (Array.isArray(data?.years) ? data.years : []);
      setGlobalYears(list);

      if (!selectedGlobalYearId) {
        const now = new Date();
        const covering = list.find((y) => {
          const s = y.startsAt ? new Date(y.startsAt) : null;
          const e = y.endsAt ? new Date(y.endsAt) : null;
          return s && e && s <= now && now <= e;
        });
        const current = covering || list.find((y) => !!y.isCurrent) || list[list.length - 1] || null;
        if (current?.id) {
          setSelectedGlobalYearId(String(current.id));
        }
      }
    } catch (error) {
      // Non-blocking: analytics can still attempt to load using server-side AY context fallback
      console.error('Failed to load global academic years:', error);
    }
  };

  const loadCrossSchoolData = async () => {
    try {
      setCrossSchoolError('');
      const params = new URLSearchParams();
      if (selectedSchool && selectedSchool !== 'all') params.set('schoolId', String(selectedSchool));
      if (selectedGlobalYearId) params.set('academicYearId', String(selectedGlobalYearId));
      const q = params.toString() ? `?${params.toString()}` : '';
      const data = await fetchJson(`/schools/cross-school-analytics${q}`);
      setCrossSchoolData(data);
    } catch (error) {
      console.error('Failed to load cross-school data:', error);
      setCrossSchoolError(error?.message || 'Failed to load cross-school analytics');
    }
  };

  const loadSchools = async () => {
    try {
      const data = await fetchJson('/schools');
      setSchools(data.schools || data || []);
    } catch (error) {
      console.error('Failed to load schools:', error);
    }
  };

  const schoolMetrics = crossSchoolData?.schoolMetrics;
  const crossTotals = crossSchoolData?.totals;
  const totalPaid = Number(crossTotals?.totalPaid ?? crossTotals?.totalRevenue ?? 0);
  const totalExpected = Number(crossTotals?.totalExpected ?? 0);
  const totalOutstanding = Number(crossTotals?.totalOutstanding ?? 0);
  const topSchoolsByRevenue = Array.isArray(crossSchoolData?.topSchoolsByRevenue)
    ? crossSchoolData.topSchoolsByRevenue
    : [];
  const revenueChartRows = Array.isArray(crossSchoolData?.schools)
    ? crossSchoolData.schools
        .map((s) => ({
          name: s?.name || `School ${s?.id || ''}`,
          revenue: Number(s?.revenue || 0),
          students: Number(s?.studentsCount || 0),
        }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10)
    : [];

  const loadSystemAlerts = async () => {
    setAlertsLoading(true);
    try {
      const data = await fetchJson('/api/super-admin/system-alerts');
      setAlerts(Array.isArray(data?.alerts) ? data.alerts : []);
    } catch (error) {
      console.error('Failed to load system alerts:', error);
    } finally {
      setAlertsLoading(false);
    }
  };


  const exportCrossSchoolReport = async () => {
    if (!canManageOverview) return;
    try {
      const response = await fetchJson('/super-admin/export-cross-school-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schoolId: selectedSchool, academicYearId: selectedGlobalYearId || null })
      });
      
      // Handle file download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cross-school-report-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Priority colors
  const priorityColors = {
    critical: theme.palette.error.main,
    high: theme.palette.warning.dark,
    medium: theme.palette.warning.light,
    low: theme.palette.info.main
  };

  // Custom tooltip for performance metrics
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Card sx={{ 
          p: 1.5, 
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          boxShadow: theme.shadows[3],
          minWidth: 200
        }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Hour {label}:00</Typography>
          {payload.map((entry, index) => (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: entry.color }}>
                {entry.name}:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {entry.name === 'errorRate' 
                  ? `${(entry.value * 100).toFixed(2)}%` 
                  : entry.name === 'responseTime'
                    ? `${entry.value.toFixed(2)}ms`
                    : entry.value.toLocaleString()
                }
              </Typography>
            </Box>
          ))}
        </Card>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  if (!hasPermission) {
    return (
      <Box p={3}>
        <Alert 
          severity="error" 
          icon={<LockIcon />}
          action={
            <Typography variant="body2" color="text.secondary">
              {t('systemOverview.contactAdmin', 'Contact your administrator for access')}
            </Typography>
          }
        >
          {t('systemOverview.accessDenied', 'Access Denied: You do not have Super Admin permissions to view this dashboard.')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          {t('systemOverview.title', 'Multi-School Analytics Dashboard')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadCrossSchoolData();
              loadSystemAlerts();
            }}
          >
            {t('systemOverview.refreshData', 'Refresh Data')}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportCrossSchoolReport}
          >
            {t('systemOverview.exportReport', 'Export Report')}
          </Button>
        </Box>
      </Box>

      {/* School Filter */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>{t('systemOverview.academicYear', 'Academic Year')}</InputLabel>
            <Select
              value={selectedGlobalYearId}
              onChange={(e) => setSelectedGlobalYearId(e.target.value)}
              label={t('systemOverview.academicYear', 'Academic Year')}
            >
              {globalYears.map((y) => (
                <MenuItem key={y.id} value={String(y.id)}>
                  {y.name}{y.isCurrent ? ' (Current)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>{t('systemOverview.filterBySchool', 'Filter by School')}</InputLabel>
            <Select
              value={selectedSchool}
              onChange={(e) => setSelectedSchool(e.target.value)}
              label={t('systemOverview.filterBySchool', 'Filter by School')}
            >
              <MenuItem value="all">{t('systemOverview.allSchools', 'All Schools')}</MenuItem>
              {schools.map(school => (
                <MenuItem key={school.id} value={school.id}>
                  {school.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Enhanced Tabs */}
      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label={t('systemOverview.platformSummary', 'Platform Summary')} />
        <Tab label={t('systemOverview.crossSchoolAnalytics', 'Cross-School Analytics')} />
        <Tab label={t('systemOverview.growthTrends', 'Growth & Trends')} />
        <Tab label={t('systemOverview.systemAlerts', 'System Alerts')} />
      </Tabs>

      {/* Platform Summary Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', boxShadow: theme.shadows[2] }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SchoolIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">{t('systemOverview.totalSchools', 'Total Schools')}</Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="primary.main">
                  {platformData?.schools?.total ?? '—'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  <Chip label={`${platformData?.schools?.active ?? 0} Active`} color="success" size="small" />
                  <Chip label={`${platformData?.schools?.pending ?? 0} Pending`} color="warning" size="small" />
                  <Chip label={`${platformData?.schools?.suspended ?? 0} Suspended`} color="error" size="small" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', boxShadow: theme.shadows[2] }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">{t('systemOverview.totalStudents', 'Total Students')}</Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="success.main">
                  {Number(platformData?.users?.students ?? 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {Number(platformData?.users?.teachers ?? 0).toLocaleString()} Teachers
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', boxShadow: theme.shadows[2] }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ChartIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">{t('systemOverview.totalUsers', 'Total Users')}</Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="info.main">
                  {Number(platformData?.users?.total ?? 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {platformData?.subscriptions?.active ?? 0} Active Subscriptions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', boxShadow: theme.shadows[2] }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">{t('systemOverview.totalRevenue', 'Total Revenue')}</Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="warning.main">
                  {`XAF ${(Number(platformData?.revenue?.total ?? 0) / 1000000).toFixed(1)}M`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('systemOverview.allTimePayments', 'All time collected payments')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Revenue by Month Chart */}
          <Grid item xs={12} md={8}>
            <Card sx={{ boxShadow: theme.shadows[2] }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  {t('systemOverview.monthlyRevenue', 'Monthly Revenue (Last 12 Months)')}
                </Typography>
                {(platformData?.revenue?.byMonth || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={platformData.revenue.byMonth.map(r => ({
                      month: new Date(r.month).toLocaleDateString('en', { month: 'short', year: '2-digit' }),
                      revenue: r.revenue
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <RechartsTooltip formatter={(v) => [`XAF ${Number(v).toLocaleString()}`, 'Revenue']} />
                      <Bar dataKey="revenue" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>{t('systemOverview.noRevenueData', 'No revenue data available yet')}</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Schools Distribution Pie */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', boxShadow: theme.shadows[2] }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Schools Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Active', value: Number(platformData?.schools?.active ?? 0), color: theme.palette.success.main },
                        { name: 'Pending', value: Number(platformData?.schools?.pending ?? 0), color: theme.palette.warning.main },
                        { name: 'Suspended', value: Number(platformData?.schools?.suspended ?? 0), color: theme.palette.error.main }
                      ].filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {[theme.palette.success.main, theme.palette.warning.main, theme.palette.error.main].map((color, index) => (
                        <Cell key={`cell-${index}`} fill={color} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => [`${value} schools`, 'Count']} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* New User Registrations (last 30 days) */}
          <Grid item xs={12}>
            <Card sx={{ boxShadow: theme.shadows[2] }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  {t('systemOverview.newUserRegistrations', 'New User Registrations (Last 30 Days)')}
                </Typography>
                {(platformData?.userGrowth || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={platformData.userGrowth.map(r => ({
                      date: new Date(r.date).toLocaleDateString('en', { month: 'short', day: 'numeric' }),
                      newUsers: r.newUsers
                    }))}>
                      <defs>
                        <linearGradient id="userGrowthGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8}/>
                          <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.05}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <RechartsTooltip />
                      <Area type="monotone" dataKey="newUsers" stroke={theme.palette.primary.main} fill="url(#userGrowthGrad)" strokeWidth={2} name="New Users" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>{t('systemOverview.noUserGrowthData', 'No user growth data available yet')}</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Cross-School Analytics Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          {crossSchoolError && (
            <Grid item xs={12}>
              <Alert severity="warning">{crossSchoolError}</Alert>
            </Grid>
          )}
          {/* Cross-School Summary Cards */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SchoolIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">{t('systemOverview.totalSchools', 'Total Schools')}</Typography>
                </Box>
                <Typography variant="h4" color="primary">
                  {Number(schoolMetrics?.total ?? (platformData?.schools?.total ?? 0))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Number(schoolMetrics?.active ?? platformData?.schools?.active ?? 0)} Active, {Number(schoolMetrics?.pending ?? platformData?.schools?.pending ?? 0)} Pending
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6">{t('systemOverview.totalStudents', 'Total Students')}</Typography>
                </Box>
                <Typography variant="h4" color="success.main">
                  {Number(crossTotals?.totalStudents ?? platformData?.users?.students ?? 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('systemOverview.acrossAllSchools', 'Across all schools')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ChartIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6">{t('systemOverview.avgStudentsPerSchool', 'Avg Students/School')}</Typography>
                </Box>
                <Typography variant="h4" color="info.main">
                  {Number(crossTotals?.avgStudentsPerSchool ?? 0).toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('systemOverview.basedOnCurrentYear', 'Based on current academic year')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CloudIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="h6">{t('systemOverview.totalRevenue', 'Total Revenue')}</Typography>
                </Box>
                <Typography variant="h4" color="warning.main">
                  {`XAF ${(totalPaid / 1000000).toFixed(1)}M`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {`Expected: XAF ${(Number(totalExpected || 0) / 1000000).toFixed(1)}M${totalOutstanding > 0 ? ` • Outstanding: XAF ${(totalOutstanding / 1000000).toFixed(1)}M` : ''}`}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* School Performance Chart */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('systemOverview.revenueBySchool', 'Revenue by School (Paid invoices)')}</Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={revenueChartRows}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="revenue" fill="#8884d8" name="Revenue" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Top Performing Schools */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('systemOverview.topSchoolsByRevenue', 'Top Schools by Revenue')}</Typography>
                <List>
                  {topSchoolsByRevenue.map((school, index) => (
                    <ListItem key={school.name || school.id || index} divider>
                      <ListItemIcon>
                        <Chip 
                          label={`#${index + 1}`} 
                          color={index === 0 ? 'primary' : index === 1 ? 'secondary' : 'default'}
                          size="small"
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={school.name}
                        secondary={school.revenue !== undefined
                          ? `${Number(school.revenue || 0).toLocaleString()} • ${Number(school.studentsCount || 0)} students`
                          : `${school.performance}% • ${school.students} students`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('systemOverview.perSchoolBreakdown', 'Per-School Breakdown')}</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('systemOverview.school', 'School')}</TableCell>
                      <TableCell align="right">{t('systemOverview.students', 'Students')}</TableCell>
                      <TableCell align="right">{t('systemOverview.expected', 'Expected')}</TableCell>
                      <TableCell align="right">{t('systemOverview.paid', 'Paid')}</TableCell>
                      <TableCell align="right">{t('systemOverview.outstanding', 'Outstanding')}</TableCell>
                      <TableCell align="right">{t('systemOverview.status', 'Status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(Array.isArray(crossSchoolData?.schools) ? crossSchoolData.schools : [])
                      .slice()
                      .sort((a, b) => Number(b?.revenue || 0) - Number(a?.revenue || 0))
                      .map((s) => (
                        <TableRow key={s.id || s.name} hover>
                          <TableCell>{s.name}</TableCell>
                          <TableCell align="right">{Number(s.studentsCount || 0).toLocaleString()}</TableCell>
                          <TableCell align="right">{`XAF ${Number(s.expectedAmount || 0).toLocaleString()}`}</TableCell>
                          <TableCell align="right">{`XAF ${Number(s.paidAmount ?? s.revenue ?? 0).toLocaleString()}`}</TableCell>
                          <TableCell align="right">{`XAF ${Number(s.outstandingAmount || Math.max(0, Number(s.expectedAmount || 0) - Number(s.paidAmount ?? s.revenue ?? 0))).toLocaleString()}`}</TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={String(s.paymentStatus || (Number(s.paidAmount ?? s.revenue ?? 0) >= Number(s.expectedAmount || 0) ? 'paid' : (Number(s.paidAmount ?? s.revenue ?? 0) > 0 ? 'partial' : 'unpaid'))).toUpperCase()}
                              color={Number(s.expectedAmount || 0) <= 0
                                ? 'default'
                                : (Number(s.paidAmount ?? s.revenue ?? 0) >= Number(s.expectedAmount || 0)
                                    ? 'success'
                                    : (Number(s.paidAmount ?? s.revenue ?? 0) > 0 ? 'warning' : 'error'))}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Performance Metrics Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom sx={{ 
              display: 'flex', 
              alignItems: 'center',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              mb: 2
            }}>
              <ChartIcon sx={{ mr: 1, fontSize: 20 }} /> {t('systemOverview.platformAnalytics', 'Platform Analytics')}
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Card sx={{ boxShadow: theme.shadows[2] }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('systemOverview.userGrowthActivity', 'User Growth & Activity (30 days)')}
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', height: 250 }}>
                  {loading ? (
                    <Skeleton variant="rectangular" height={250} />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={platformData?.userGrowth || []} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                        <XAxis dataKey="date" />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <RechartsTooltip 
                          contentStyle={{ 
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: theme.shape.borderRadius,
                            border: 'none',
                            boxShadow: theme.shadows[3]
                          }}
                        />
                        <Legend />
                        <Bar 
                          yAxisId="left"
                          dataKey="users" 
                          name="Active Users"
                          fill={theme.palette.primary.main}
                          barSize={20}
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="newUsers" 
                          name="New Users"
                          stroke={theme.palette.success.main}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', boxShadow: theme.shadows[2] }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      {t('systemOverview.schoolsDistribution', 'Schools Distribution')}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', height: 300}}>
                    {loading ? (
                      <Skeleton variant="rectangular" height={300} />
                    ) : (
                      <ResponsiveContainer width="100%" height="100%" padding={3}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Active', value: Number(schoolMetrics?.active ?? platformData?.schools?.active ?? 0), color: theme.palette.success.main },
                              { name: 'Pending', value: Number(schoolMetrics?.pending ?? platformData?.schools?.pending ?? 0), color: theme.palette.warning.main },
                              { name: 'Suspended', value: Number(schoolMetrics?.needsAttention ?? 0), color: theme.palette.error.main }
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            labelLine={false}
                            padding={3}
                          >
                            {[
                              theme.palette.success.main,
                              theme.palette.warning.main,
                              theme.palette.error.main
                            ].map((color, index) => (
                              <Cell key={`cell-${index}`} fill={color} />
                            ))}
                          </Pie>
                          <RechartsTooltip 
                            formatter={(value) => [`${value} schools`, 'Count']}
                            contentStyle={{ 
                              backgroundColor: theme.palette.background.paper,
                              borderRadius: theme.shape.borderRadius,
                              boxShadow: theme.shadows[3]
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', boxShadow: theme.shadows[2] }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      {t('systemOverview.storageUsage', 'Storage Usage by Institution Type')}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', height: 300 }}>
                    {loading ? (
                      <Skeleton variant="rectangular" height={300} />
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={platformData?.schoolGrowth?.map(r => ({ name: new Date(r.month).toLocaleDateString('en', { month: 'short' }), value: r.schools })) || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                          <XAxis dataKey="name" />
                          <YAxis unit="GB" />
                          <RechartsTooltip 
                            formatter={(value) => [`${value} GB`, 'Storage']}
                            contentStyle={{ 
                              backgroundColor: theme.palette.background.paper,
                              borderRadius: theme.shape.borderRadius,
                              boxShadow: theme.shadows[3]
                            }}
                          />
                          <Bar 
                            dataKey="value" 
                            name="Storage Used"
                            barSize={60}
                            radius={[4, 4, 0, 0]}
                          >
                            {(platformData?.schoolGrowth || []).map((_, index) => (
                              <Cell key={`cell-${index}`} fill={index % 2 === 0 ? theme.palette.primary.main : theme.palette.secondary.main} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

          <Grid item xs={12}>
            <Card sx={{ boxShadow: theme.shadows[2] }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('systemOverview.systemPerformance', 'System Performance Metrics (24h)')}
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', height: 350, padding: 3 }}>
                  {loading ? (
                    <Skeleton variant="rectangular" height={350} />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%" >
                      <ComposedChart data={platformData?.recentActivity || []} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                        <XAxis dataKey="hour" label={{ value: 'Hour of Day', position: 'insideBottom', offset: -5 }} />
                        <YAxis yAxisId="left" label={{ value: 'Response Time (ms)', angle: -90, position: 'insideLeft' }} />
                        <YAxis 
                          yAxisId="right" 
                          orientation="right" 
                          label={{ value: 'Error Rate (%)', angle: 90, position: 'insideRight'}}
                          domain={[0, 0.6]}
                          tickFormatter={(value) => `${(value * 100).toFixed(1)}%`}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="responseTime" 
                          name="Response Time"
                          stroke={theme.palette.primary.main}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
                          sx={{pb: 1}}
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="errorRate" 
                          name="Error Rate"
                          stroke={theme.palette.error.main}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
                        />
                        <Bar 
                          yAxisId="left"
                          dataKey="throughput" 
                          name="Requests"
                          fill={theme.palette.info.light}
                          barSize={20}
                          opacity={0.7}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ boxShadow: theme.shadows[2] }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('systemOverview.moduleAdoptionRate', 'Module Adoption Rate')}
                </Typography>
                {loading ? (
                  <Skeleton variant="rectangular" height={100} />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                    {(platformData?.schoolGrowth || []).slice(0, 5).map((item, index) => ({ name: new Date(item.month).toLocaleDateString('en', { month: 'short' }), value: item.schools })).map((module, index) => (
                      <Box key={index} sx={{ flex: 1, minWidth: 180 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>{module.name}</Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={module.value} 
                          sx={{ 
                            height: 12, 
                            borderRadius: 5,
                            backgroundColor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 5,
                              backgroundColor: index % 2 === 0 
                                ? theme.palette.primary.main 
                                : theme.palette.secondary.main
                            }
                          }} 
                        />
                        <Typography variant="body1" sx={{ mt: 1, fontWeight: 600, textAlign: 'center' }}>
                          {module.value}%
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* System Alerts Tab */}
      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 2 }}>
              <Typography variant="h6">{t('systemOverview.systemAlertsNotifications', 'System Alerts & Notifications')}</Typography>
              <Button size="small" startIcon={<RefreshIcon />} onClick={loadSystemAlerts} disabled={alertsLoading}>
                {alertsLoading ? t('systemOverview.refreshing', 'Refreshing...') : t('systemOverview.refresh', 'Refresh')}
              </Button>
            </Box>
            {alerts.length === 0 && !alertsLoading ? (
              <Alert severity="success" sx={{ mt: 2 }}>{t('systemOverview.noAlerts', 'No active alerts — all systems are healthy.')}</Alert>
            ) : (
              <List>
                {alerts.map((alert) => (
                  <React.Fragment key={alert.id}>
                    <ListItem sx={{
                      backgroundColor: priorityColors[alert.priority] + '15',
                      borderLeft: `4px solid ${priorityColors[alert.priority]}`,
                      py: 2,
                      transition: 'background-color 0.2s',
                      '&:hover': { backgroundColor: priorityColors[alert.priority] + '25' }
                    }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {alert.type === 'subscription' && <AccessTimeIcon sx={{ color: priorityColors[alert.priority] }} />}
                        {alert.type === 'support' && <WarningIcon sx={{ color: priorityColors[alert.priority] }} />}
                        {alert.type === 'payment' && <SecurityIcon sx={{ color: priorityColors[alert.priority] }} />}
                        {alert.type === 'school' && <SchoolIcon sx={{ color: priorityColors[alert.priority] }} />}
                      </ListItemIcon>
                      <ListItemText
                        primary={alert.message}
                        secondary={alert.time ? new Date(alert.time).toLocaleDateString() : ''}
                        primaryTypographyProps={{ fontWeight: 600, color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip label={String(alert.type || '').toUpperCase()} size="small" variant="outlined" />
                        <Chip
                          label={String(alert.priority || '').toUpperCase()}
                          size="small"
                          sx={{
                            backgroundColor: priorityColors[alert.priority] + '30',
                            color: priorityColors[alert.priority],
                            fontWeight: 700, letterSpacing: 0.5
                          }}
                        />
                      </Box>
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bulk Operations removed */}
    </Box>
  );
}